import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ShieldCheck, Search, MapPin, TrendingUp, ShoppingCart,
  Brain, Smartphone, Briefcase, Lock, Award,
} from 'lucide-react';
import PublicNav from '@/components/PublicNav';
import PublicFooter from '@/components/PublicFooter';
import XVAssistWidget from '@/components/XVAssistWidget';

const STAT_CARDS = [
  { label: '9 Provinces Covered', desc: 'Nationwide reach across all South African rugby provinces' },
  { label: '3 Scout Tiers', desc: 'Flexible access levels for scouts, agencies, and unions' },
  { label: 'R19–R79 / player / month', desc: 'Accessible institutional pricing across all tiers' },
  { label: '0 Cost for Players', desc: 'Free for every player — always, no exceptions' },
  { label: 'AI XV Assist Powered', desc: 'Persistent rugby AI mentor on every dashboard, ready to coach you' },
  { label: 'POPIA Compliant', desc: 'Minor data protected with role-based access and full audit logging' },
];

const CORE_CAPS = [
  { icon: ShieldCheck, t: 'Verified Profiles', d: 'Multi-layer identity & coach verification for every player' },
  { icon: TrendingUp,  t: 'Performance Tracking', d: 'PDS scores, fitness, match stats & video in one place' },
  { icon: Search,      t: 'Scout Discovery', d: 'AI-powered talent search across all 9 provinces' },
  { icon: Briefcase,   t: 'Team Infrastructure', d: 'Full operational stack for schools, clubs & unions' },
  { icon: Brain,       t: 'XV Assist AI', d: 'Persistent SA-rugby AI mentor on every dashboard — programmes, drills, advice.' },
  { icon: Smartphone,  t: 'Mobile-First', d: 'Optimised for Android & low-data environments across SA' },
  { icon: Lock,        t: 'POPIA Secure', d: 'Minor data protected; role-based access; full audit logging' },
  { icon: Award,       t: 'Partner Network', d: 'World of Rugby / TryAndScore — 5% off gear for all users' },
];

const HERO_FEATURES = [
  { icon: ShieldCheck, t: 'Verified Profiles', d: 'Trusted players, coaches, schools & clubs.' },
  { icon: Search,      t: 'AI Scout Discovery', d: 'Find the right talent faster with AI.' },
  { icon: MapPin,      t: 'National Talent Map', d: 'Talent from every province. No player left behind.' },
  { icon: TrendingUp,  t: 'Development Pathways', d: 'Tools, guidance & support to grow and succeed.' },
  { icon: Brain,       t: 'XV Assist AI', d: 'A rugby mentor in your pocket. Trained for SA.' },
];

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white" data-testid="landing-page">
      <PublicNav />

      {/* HERO */}
      <section className="pt-28 pb-12 px-4 bg-black" data-testid="hero-section">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="font-heading font-black tracking-tight text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95]" data-testid="hero-heading">
              Discover.<br />
              <span className="text-[#A3FF00]">Develop.</span><br />
              Rise.
            </h1>
            <p className="mt-7 text-lg text-zinc-300 max-w-md leading-relaxed">
              South Africa&rsquo;s National Digital Rugby Discovery &amp; Development Platform
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button onClick={() => navigate('/auth?role=player')} className="bg-[#A3FF00] hover:bg-[#88dd00] text-black font-bold px-7 py-6 text-base" data-testid="cta-join-player">Join as Player</Button>
              <Button onClick={() => navigate('/auth?role=scout')} variant="outline" className="border-zinc-700 text-white hover:bg-zinc-900 px-7 py-6 text-base bg-transparent" data-testid="cta-im-scout">I&rsquo;m a Scout</Button>
              <Button onClick={() => navigate('/auth?role=school_club')} variant="outline" className="border-[#A3FF00] text-[#A3FF00] hover:bg-[#A3FF00] hover:text-black px-7 py-6 text-base bg-transparent" data-testid="cta-register-school">Register Your School or Club</Button>
            </div>
          </div>
          <div className="relative">
            {/* Dynamic shade — radial vignette + subtle green glow + bottom fade into the black bg */}
            <div className="relative rounded-2xl overflow-hidden">
              <img src="/hero_team.png" alt="FutureXV — discover, develop, rise" className="hero-parallax w-full h-auto object-contain" data-testid="hero-image" />
              {/* Vignette */}
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_45%,_rgba(0,0,0,0.55)_100%)]" />
              {/* Edge fade into page bg */}
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black to-transparent" />
              <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-black to-transparent" />
              <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-black to-transparent" />
              {/* Subtle accent glow */}
              <div className="hero-accent-glow pointer-events-none absolute -inset-4 -z-10 bg-[radial-gradient(circle_at_70%_40%,_rgba(163,255,0,0.18),_transparent_60%)] blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* PROUD PARTNER */}
      <section className="px-4 pb-12" data-testid="partner-section">
        <div className="max-w-[1400px] mx-auto rounded-xl border border-zinc-800 bg-[#0a0a0a] overflow-hidden flex flex-col md:flex-row items-stretch">
          <a
            href="https://tryandscore.co.za"
            target="_blank"
            rel="noopener noreferrer"
            className="md:w-1/2 flex items-center justify-center p-5"
            data-testid="tryandscore-logo-link"
          >
            <div className="bg-white rounded-lg p-3">
              <img src="/tryandscore_logo.jpg" alt="TryAndScore Sports — World of Rugby" className="w-full max-w-[560px] h-auto object-contain" />
            </div>
          </a>
          <div className="md:w-1/2 flex flex-col md:flex-row items-center gap-6 px-6 md:px-10 py-7">
            <div className="flex-1 text-center md:text-left">
              <p className="text-[#A3FF00] text-xs font-bold tracking-[0.2em] mb-1">PROUD PARTNER</p>
              <h3 className="text-2xl md:text-3xl font-heading font-bold text-white leading-tight">
                Official Rugby Retail &amp; Equipment Partner
              </h3>
              <p className="mt-2 text-zinc-300 text-sm">
                Equipping Future Champions &nbsp;·&nbsp;
                <span className="inline-block bg-[#A3FF00]/10 border border-[#A3FF00]/30 text-[#A3FF00] font-bold rounded-md px-2.5 py-0.5 mr-1">5% OFF</span>
                with code <span className="font-mono font-bold text-[#A3FF00]" data-testid="partner-promo-code">FUTUREXV</span>
              </p>
            </div>
            <a
              href="https://tryandscore.co.za"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="shop-now-link"
              className="inline-flex items-center gap-2 border-2 border-[#A3FF00] text-[#A3FF00] hover:bg-[#A3FF00] hover:text-black font-bold px-7 py-3.5 rounded-md transition-colors shrink-0"
            >
              Shop Now <ShoppingCart className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* 5-ICON ROW */}
      <section className="px-4 pb-20" data-testid="hero-features">
        <div className="max-w-[1400px] mx-auto grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-4">
          {HERO_FEATURES.map(({ icon: Icon, t, d }) => (
            <div key={t} className="text-center" data-testid={`hero-feature-${t.toLowerCase().replace(/\s/g,'-')}`}>
              <Icon className="w-12 h-12 mx-auto text-[#A3FF00]" strokeWidth={1.5} />
              <p className="mt-3 font-bold text-white">{t}</p>
              <p className="text-xs text-zinc-400 mt-1.5 leading-relaxed max-w-[180px] mx-auto">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BY THE NUMBERS */}
      <section className="px-4 pb-20 border-t border-zinc-900 pt-20" data-testid="by-the-numbers">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-3">By the Numbers</h2>
          <p className="text-zinc-400 text-center max-w-2xl mx-auto mb-12">
            FutureXV is built to serve every level of South African rugby — from grassroots players in rural communities to provincial unions managing thousands of athletes.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {STAT_CARDS.map(({ label, desc }) => (
              <div key={label} className="bg-[#0a0a0a] border border-zinc-800 hover:border-[#A3FF00]/40 transition-colors rounded-lg p-6">
                <p className="text-[#A3FF00] font-bold text-lg">{label}</p>
                <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CORE CAPABILITIES */}
      <section className="px-4 pb-20" data-testid="core-capabilities">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-3">What FutureXV Does</h2>
          <p className="text-zinc-400 text-center max-w-2xl mx-auto mb-12">
            Ten core capabilities — all in one platform — built to serve every stakeholder in South African rugby.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CORE_CAPS.map(({ icon: Icon, t, d }) => (
              <div key={t} className="bg-[#0a0a0a] border border-zinc-800 hover:border-[#A3FF00]/40 transition-all rounded-lg p-5">
                <Icon className="w-8 h-8 text-[#A3FF00] mb-3" strokeWidth={1.5} />
                <p className="font-bold text-white text-sm">{t}</p>
                <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-4 pb-20" data-testid="final-cta">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Ready to be discovered?</h2>
          <p className="text-zinc-400 mb-7">Free forever for players. Accessible pricing for institutions. Questions? <a href="mailto:info@futurexv.co.za" className="text-[#A3FF00] hover:underline">info@futurexv.co.za</a></p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button onClick={() => navigate('/auth')} className="bg-[#A3FF00] hover:bg-[#88dd00] text-black font-bold px-8 py-6 text-base" data-testid="final-cta-get-started">Get Started Free</Button>
            <Button onClick={() => navigate('/pricing')} variant="outline" className="border-zinc-700 text-zinc-200 hover:bg-zinc-900 px-8 py-6 text-base" data-testid="final-cta-pricing">View Pricing</Button>
          </div>
        </div>
      </section>

      <PublicFooter />
      <XVAssistWidget forcePublic />
    </div>
  );
}
